import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";

/** Return today's date as YYYY-MM-DD in the browser's local timezone. */
export function todayISO() {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** Add `delta` days to a YYYY-MM-DD string and return YYYY-MM-DD. */
export function addDaysISO(iso, delta) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

/** Format YYYY-MM-DD as "Mon · Jul 22" (browser locale). */
export function formatDateLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/**
 * DateNav — compact date picker with prev/today/next arrows + native date input.
 *
 * Props:
 *   date         current ISO date (YYYY-MM-DD)
 *   onChange(iso) called when the user picks any date
 *   testid       optional testid prefix (default "datenav")
 */
export default function DateNav({ date, onChange, testid = "datenav" }) {
  const today = todayISO();
  const isToday = date === today;
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      <button
        onClick={() => onChange(addDaysISO(date, -1))}
        className="btn btn-outline"
        style={{ padding: "6px 8px" }}
        title="Previous day"
        data-testid={`${testid}-prev`}
      >
        <ChevronLeft size={14} />
      </button>
      <label className="btn btn-outline relative cursor-pointer" style={{ padding: "6px 12px", gap: 6 }} data-testid={`${testid}-picker`}>
        <Calendar size={12} style={{ color: "var(--green)" }} />
        <span className="mono text-[12px]">{formatDateLabel(date)}</span>
        <input
          type="date"
          value={date}
          onChange={(e) => e.target.value && onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
          data-testid={`${testid}-input`}
        />
      </label>
      <button
        onClick={() => onChange(addDaysISO(date, 1))}
        className="btn btn-outline"
        style={{ padding: "6px 8px" }}
        title="Next day"
        data-testid={`${testid}-next`}
      >
        <ChevronRight size={14} />
      </button>
      {!isToday && (
        <button
          onClick={() => onChange(today)}
          className="btn btn-outline"
          style={{ padding: "6px 10px", color: "var(--green)", borderColor: "var(--green-border)" }}
          data-testid={`${testid}-today`}
        >
          Today
        </button>
      )}
    </div>
  );
}
